import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaChartLine, FaBoxes, FaUsers } from 'react-icons/fa';
import './AdminHome.css';
import { useProducts } from '../../../store/ProductContext';
import { useUser } from '../../../store/UserContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebase/Config';
import LoadingForAll from '../../../Pages/LoadingForAll';

function AdminHome() {
  const navigate = useNavigate();
  const { products, loading, error, deleteProduct } = useProducts();
  const { userData } = useUser();

  const [totalUsers, setTotalUsers] = useState([]);
  const [totalOrders, setTotalOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);

  useEffect(() => {
    const fetchTotalUsers = async () => {
      const userRef = collection(db, 'users');
      const querySnapshot = await getDocs(userRef);
      setTotalUsers(querySnapshot.size);
    };

    const fetchTotalOrders = async () => {
      const orderRef = collection(db, 'orders');
      const querySnapshot = await getDocs(orderRef);
      setTotalOrders(querySnapshot.size);
    };
    fetchTotalUsers();
    fetchTotalOrders();
  }, [products, userData]);

  // Filter products based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product => 
        product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  if (loading) return <div><LoadingForAll /></div>;
  if (error) return <div>Error: {error}</div>;

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(id);
      } catch (err) {
        alert('Error deleting product: ' + err.message);
      }
    }
  };

  return (
    <div className="admin-container">
      {/* Admin Header */}
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-actions">
          <button className="btn-primary" onClick={() => navigate('/add-products')}>
            <FaPlus /> Add Product
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card ">
          <div className="stat-icon">
            <FaChartLine />
          </div>
          <div className="stat-info ">
            <h3>Total Sales</h3>
            <p>{totalOrders}</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FaBoxes />
          </div>
          <div className="stat-info">
            <h3>Total Products</h3>
            <p>{products.length}</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FaUsers />
          </div>
          <div className="stat-info">
            <h3>Total Users</h3>
            <p>{totalUsers}</p>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <section className="products-section">
        <div className="section-header">
          <h2>Products Management</h2>
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input 
              type="text" 
              placeholder="Search products..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="products-table-container">
          <table className="products-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Product</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Total Orders</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product) => (
                  <tr key={product.id}>
                    <td>{product.id}</td>
                    <td className="product-cell">
                      <img 
                        src={product.imageUrls?.imageUrl1}
                        alt={product.title} 
                        className="product-thumbnail"
                      />
                      <span>{product.title}</span>
                    </td>
                    <td>{product.category}</td>
                    <td>{product.pricing.ourPrice}</td>
                    <td>{product.stock}</td>
                    <td>{product.totalOrders || 0}</td>
                    <td className="actions-cell">
                      <button className="btn-edit" onClick={() => navigate(`/edit-product/${product.id}`)}>
                        <FaEdit />
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(product.id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="no-results">
                    No products found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default AdminHome;